// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

export type WizardValidationError =
    | { code: 'NO_SOURCES' }
    | { code: 'NO_DESTINATIONS' }
    | { code: 'DUPLICATE_SOURCE'; sourceIds: number[] }
    | { code: 'DUPLICATE_DESTINATION_CAGES'; duplicateCageIds: number[] }
    | { code: 'MISSING_DESTINATION_NAME'; destinationTempId: string }
    | { code: 'MISSING_DESTINATION_CAGE'; destinationTempId: string }
    | { code: 'NO_INFLOWS'; destinationTempId: string }
    | {
          code: 'DUPLICATE_INFLOWS_IN_DESTINATION';
          destinationTempId: string;
          duplicateSourceIds: number[];
      }
    | { code: 'SOURCE_NOT_CONSUMED'; sourceFishGroupId: number };

export interface ValidateDestinationsInput {
    sources: { fishGroupId: number }[];
    destinations: {
        tempId: string;
        name: string;
        cageId: number | null;
        receivesFrom: { sourceFishGroupId: number }[];
    }[];
}

/**
 * Collect every wizard-level validation issue. The UI feeds the result into
 * a banner on the destinations step so the operator sees the full picture
 * before hitting Next.
 *
 * Mirrors the backend's `assertSourcesDestinationsCoverage` rules and adds two
 * UI-only rules (missing name, missing cage) that prevent submitting a form
 * the backend would reject with a 400.
 */
export function validateDestinations(input: ValidateDestinationsInput): WizardValidationError[] {
    const { sources, destinations } = input;
    const errors: WizardValidationError[] = [];

    if (sources.length === 0) {
        errors.push({ code: 'NO_SOURCES' });
    }
    if (destinations.length === 0) {
        errors.push({ code: 'NO_DESTINATIONS' });
    }

    // Duplicate source ids — unlikely from the UI (a checkbox can't
    // toggle the same FG twice) but the backend checks for it and so do we.
    const sourceCount = new Map<number, number>();
    for (const s of sources) {
        sourceCount.set(s.fishGroupId, (sourceCount.get(s.fishGroupId) ?? 0) + 1);
    }
    const duplicateSourceIds = [...sourceCount.entries()]
        .filter(([, count]) => count > 1)
        .map(([id]) => id);
    if (duplicateSourceIds.length > 0) {
        errors.push({ code: 'DUPLICATE_SOURCE', sourceIds: duplicateSourceIds });
    }

    // Two destinations targeting the same cage would create two open
    // location rows for that cage — the backend's partial unique index
    // would reject the second one, but we surface it earlier with a
    // human-readable error.
    const cageCount = new Map<number, number>();
    for (const d of destinations) {
        if (d.cageId == null) continue;
        cageCount.set(d.cageId, (cageCount.get(d.cageId) ?? 0) + 1);
    }
    const duplicateCageIds = [...cageCount.entries()]
        .filter(([, count]) => count > 1)
        .map(([id]) => id);
    if (duplicateCageIds.length > 0) {
        errors.push({ code: 'DUPLICATE_DESTINATION_CAGES', duplicateCageIds });
    }

    const uniqueSourceIds = new Set(sources.map((s) => s.fishGroupId));
    const consumedSources = new Set<number>();

    for (const dest of destinations) {
        if (dest.name.trim().length === 0) {
            errors.push({ code: 'MISSING_DESTINATION_NAME', destinationTempId: dest.tempId });
        }
        if (dest.cageId == null) {
            errors.push({ code: 'MISSING_DESTINATION_CAGE', destinationTempId: dest.tempId });
        }
        if (dest.receivesFrom.length === 0) {
            errors.push({ code: 'NO_INFLOWS', destinationTempId: dest.tempId });
            continue;
        }

        // Duplicate inflows within a single destination would inflate
        // fish_group_transfers with two rows for the same logical edge.
        const inflowCount = new Map<number, number>();
        for (const inflow of dest.receivesFrom) {
            inflowCount.set(
                inflow.sourceFishGroupId,
                (inflowCount.get(inflow.sourceFishGroupId) ?? 0) + 1
            );
        }
        const duplicateInflowIds = [...inflowCount.entries()]
            .filter(([, count]) => count > 1)
            .map(([id]) => id);
        if (duplicateInflowIds.length > 0) {
            errors.push({
                code: 'DUPLICATE_INFLOWS_IN_DESTINATION',
                destinationTempId: dest.tempId,
                duplicateSourceIds: duplicateInflowIds,
            });
        }

        for (const inflow of dest.receivesFrom) {
            if (uniqueSourceIds.has(inflow.sourceFishGroupId)) {
                consumedSources.add(inflow.sourceFishGroupId);
            }
            // Note: an inflow referencing an unlisted source cannot happen
            // from the UI today (the checkboxes are sourced from `sources`),
            // so we don't surface that case as a separate error here.
        }
    }

    for (const sourceId of uniqueSourceIds) {
        if (!consumedSources.has(sourceId)) {
            errors.push({ code: 'SOURCE_NOT_CONSUMED', sourceFishGroupId: sourceId });
        }
    }

    return errors;
}
